import { FollowUp } from '@app/modules/follow-ups/follow-ups.types';
import { CancerscreenServiceInformation } from '@app/modules/service-data/types/cancerscreen.types';
import { CarrierServiceInformation } from '@app/modules/service-data/types/carrier.types';
import { GalleriServiceInformation } from '@app/modules/service-data/types/galleri.types';
import { MonoServiceInformation } from '@app/modules/service-data/types/mono.types';
import { NegSingleCarrierServiceInformation } from '@app/modules/service-data/types/neg-single-carrier.types';
import { NipptServiceInformation } from '@app/modules/service-data/types/nippt.types';
import { NiptServiceInformation } from '@app/modules/service-data/types/nipt.types';
import { PaternityServiceInformation } from '@app/modules/service-data/types/paternity.types';
import { RhdServiceInformation } from '@app/modules/service-data/types/rhd.types';
import {
  ExtendedSample,
  Sample,
  SampleExtensions,
} from '@app/modules/service-data/types/sample.types';
import { VenisafeServiceInformation } from '@app/modules/service-data/types/venisafe.types';
import {
  Bundle,
  ExtendedInvoice,
  Partner,
  PaymentType,
  Product,
  ProductInventory,
  Properties,
} from '@core/core.types';
import { OtherServiceInformation } from './other.types';
import { SharedServiceInformation } from './shared.types';

export interface Timestamp {
  by: string;
  timestamp: string;
}

export interface ServiceData {
  id: string;
  referenceKey: string;
  partnerId: string;
  bundleIds: string[];
  sampleIds: string[];
  patientInformation: PatientInformation;
  paymentInformation: PaymentInformation;
  serviceInformation: ServiceInformation;
  followUpIds: string[];
  notes: string;
  generated?: Date;
  incomplete?: Date;
  finished?: Date;
  status: string;
  statusChanged: Date;
  statusChangedBy: string;
  dateOfService?: Date;
  onlineReports: boolean;
  reportLanguageId?: string;
}

export interface ExtendedServiceInformation extends ServiceInformation {
  shared?: SharedServiceInformation;
}

export interface SharedServiceData extends ServiceData {
  serviceInformation: ExtendedServiceInformation;
}

export interface PatientInformation {
  firstName: string;
  lastName: string;
  documentNumber?: string;
  dateOfBirth: string;
  email: string;
  phoneNumber: string;
  height: number;
  weight: number;
  address?: PatientAddress | null;
}

export interface PatientAddress {
  country: string;
  street: string;
  zip: string;
  city: string;
}

export interface PaymentInformation {
  paymentType: PaymentType;
  invoiceId?: string;
  paid?: Date;
  geneplanetInvoiceNumber?: string;
  doctorInvoiceNumber?: string;
  laboratoryInvoiceNumber?: string;
  notes?: string;
}

export interface ServiceInformation {
  nipt?: NiptServiceInformation;
  nippt?: NipptServiceInformation;
  rhd?: RhdServiceInformation;
  carrier?: CarrierServiceInformation;
  mono?: MonoServiceInformation;
  venisafe?: VenisafeServiceInformation;
  cancerScreen?: CancerscreenServiceInformation;
  galleri?: GalleriServiceInformation;
  paternity?: PaternityServiceInformation;
  negSingleCarrier?: NegSingleCarrierServiceInformation;
  other?: OtherServiceInformation;
}

export interface DoctorInformation {
  doctorId: string;
  doctorConsent: boolean;
}

export interface DiseaseHistory {
  hasHistory: boolean;
  diseases: DiseaseInformation[];
}

export interface FamilyDiseaseHistory extends DiseaseHistory {
  diseases: FamilyDiseaseInformation[];
}

export interface DiseaseInformation {
  conditionDescription: string;
  ageOfOnset?: number;
}

export interface FamilyDiseaseInformation extends DiseaseInformation {
  relationToPatient: string;
}

export interface CancerHistory {
  hasHistory: boolean;
  status: CancerHistoryStatus;
}

export enum CancerHistoryStatus {
  Recovered = 'Recovered',
  CurrentlyAffected = 'CurrentlyAffected',
}

export interface TherapyInformation {
  hasReceived: boolean;
  isRelevant: boolean;
}

export enum PregnancyType {
  singleton = 'Singleton',
  twins = 'Twins',
}

export enum ChorinicityType {
  DCDA = 'DCDA',
  MCDA = 'MCDA',
  MCMA = 'MCMA',
}

export interface PriorScreeningTest {
  priorScreeningTestType: PriorScreeningTestType;
  firstTRisks: Risks;
  secondTRisks: Risks;
}

export enum PriorScreeningTestType {
  BiochemistryFirstTrimester = 'BiochemistryFirstTrimester',
  FirstAndSecondTrimesterIntegratedTest = 'FirstAndSecondTrimesterIntegratedTest',
  NoTestTaken = 'NoTestTaken',
  Other = 'Other',
  SecondTrimester = 'SecondTrimester',
  UltrasoundBiochemistryFirstTrimester = 'UltrasoundBiochemistryFirstTrimester',
  UltrasoundFirstTrimester = 'UltrasoundFirstTrimester',
}

export enum Ethnicity {
  African = 'African',
  Asian = 'Asian',
  Caucasian = 'Caucasian',
  Hispanic = 'Hispanic',
  MiddleEastern = 'MiddleEastern',
  NativeAmerican = 'NativeAmerican',
  PacificIslander = 'PacificIslander',
  Other = 'Other',
}

export interface Risks {
  t21Risk: number;
  t18Risk: number;
  t13Risk: number;
}

export interface GestationAge {
  week: number;
  day: number;
}

export interface VanishingTwinSyndrome {
  isDetected: boolean;
  detectedGestationAge: GestationAge;
}

export interface InVitro {
  isInVitroFertilized: boolean;
  vitroType: InVitroTypes;
  isSurrogateMother: boolean;
}

export enum InVitroTypes {
  OwnEgg = 'OwnEgg',
  DonorEgg = 'DonorEgg',
}

export interface ExtendedServiceData extends ServiceData {
  partner?: Partner;
  doctor?: Partner;
  bundles?: Bundle[];
  invoice?: ExtendedInvoice;
  samples?: Sample[];
  followUps?: FollowUp[];
}

export type ProvisionServiceData = Omit<
  ServiceData,
  | 'sampleIds'
  | 'referenceKey'
  | 'status'
  | 'statusChanged'
  | 'statusChangedBy'
  | 'followUpIds'
>;

export interface ExtendedProductInventoryItem extends ProductInventory {
  partner?: Partner;
  product?: Product;
  sample?: ExtendedSample;
}

export interface DocumentInteraction {
  interaction: string;
  interacted: string;
  interactedBy: string;
}

export enum DocumentStatus {
  uploaded = 'Uploaded',
  approved = 'Approved',
  downloaded = 'Downloaded',
  disapproved = 'Disapproved',
  archived = 'Archived',
}

export interface Document {
  id: string;
  typeId: string;
  fileName: string;
  extension: string;
  sizeInBytes: string;
  interactions?: DocumentInteraction[];
  approved?: Timestamp;
  generated: Timestamp;
  lastStatus: DocumentStatus;
  lastStatusChanged: string;
  lastStatusChangedBy: string;
  disapproved?: Timestamp;
  archived?: Timestamp;
  properties: Properties[];
  uploaded?: { by: string };
}

export interface ExtendedDocument extends Document {
  productInventoryItem?: ExtendedProductInventoryItem;
}

export enum DocumentType {
  NoCall = 'NoCall',
  Refund = 'Refund',
  Resample = 'Resample',
  NotQualified = 'NotQualified',
  OfficialReport = 'OfficialReport',
  HighRisk = 'HighRisk',
  Delay = 'Delay',
  Canceled = 'Canceled',
}

export enum DocumentDescription {
  ByLaboratory = 'ByLaboratory',
  ByPartner = 'ByPartner',
}

export enum DocumentNotQualifiedKeywords {
  NotQualified = 'notqualified',
  NotQualifiedByPartner = 'notqualified-bypartner',
  NotQualifiedByLaboratory = 'notqualified-bylaboratory',
  Canceled = 'canceled',
}

export enum DocumentNotQualifiedType {
  Hemolysis = 'Hemolysis',
  InsufficientVolume = 'InsufficientVolume',
  TestLimitation = 'TestLimitation',
  ZGLabMistake = 'ZGLabMistake',
  KitMixup = 'KitMixup',
  NinetySixHPlus = '96h+',
  DamagedTube = 'DamagedTube',
  DHL = 'DHL',
  Other = 'Other',
  Expired = 'Expired',
  DnaQuality = 'DnaQuality',
}

export enum DocumentKeywords {
  resample1 = 're-sample',
  resample2 = 'resample',
  resample3 = 'resampling',
  official = 'official',
  nocall = 'nocall',
  refund = 'refund',
  delay = 'delay',
  highRisk1 = 'high risk',
  highRisk2 = 'high-risk',
  notqualified = 'notqualified',
  notqualifiedbypartner = 'notqualified-bypartner',
  notqualifiedbylaboratory = 'notqualified-bylaboratory',
  canceled = 'canceled',
}

export const NonOfficialDocumentReportTypes = [
  DocumentKeywords.resample1,
  DocumentKeywords.resample2,
  DocumentKeywords.nocall,
  DocumentKeywords.refund,
  DocumentKeywords.delay,
  DocumentKeywords.highRisk1,
  DocumentKeywords.highRisk2,
  DocumentKeywords.notqualifiedbylaboratory,
  DocumentKeywords.notqualifiedbypartner,
  DocumentKeywords.notqualified,
];

export interface DocumentApprovalRequest {
  subject: string;
  body: string;
  recipients: string[];
  ccRecipients: string[];
  bccRecipients: string[];
  sendEmail: boolean;
}

export enum ProductInventoryItemStatus {
  generated = 'Generated',
  processingStarted = 'ProcessingStarted',
  canceled = 'Canceled',
  processingFinished = 'ProcessingFinished',
  processingFailed = 'ProcessingFailed',
}

export interface ServiceDataExtensions {
  partner?: boolean;
  doctor?: boolean;
  bundles?: boolean;
  invoice?: boolean;
  samples?: boolean;
  followUps?: boolean;
}

export interface ProductInventoryItemExtensions {
  partner?: boolean;
  product?: boolean;
  sample?: SampleExtensions;
}

export interface DocumentExtensions {
  pii?: ProductInventoryItemExtensions;
}

export interface BgiXlsResult {
  KitId: string;
  Result: BgiResult;
  ReportType: string;
  ResultRisk?: Risk;
  index?: number;
  fileName?: string;
  sourceFileName?: string;
  Signee?: string;
}

export interface BgiResult {
  Trisomy21Risk: Risk;
  Trisomy18Risk: Risk;
  Trisomy13Risk: Risk;
  FetalFraction: string;
  Trisomy9Risk: Risk;
  Trisomy22Risk: Risk;
  Trisomy16Risk: Risk;
  XoResult: ChromosomeDetection;
  XxxResult: ChromosomeDetection;
  XyyResult: ChromosomeDetection;
  XxyResult: ChromosomeDetection;
  MicrodeletionsDuplications: string;
  IncidentalFindings: string;
  Dated: moment.Moment;
  SampleReceived: moment.Moment;
  YChromosome: ChromosomeDetection | string;
  TireType: PregnancyType;
}

export interface ReportResult {
  SubLibraryId: string;
  KitId: string;
  OperationAdvice: string;
  ReasonForQualityControlFailure: string;
  TestChr13: string;
  TestChr18: string;
  TestChr21: string;
  TestSexChr: string;
  FetalFraction: number;
  OtherAutosomes: string;
  Disease: string;
  DelDup: string;
  FetusGender: string;
}

export interface GeneratedReport {
  Data: string;
  FileName: string;
  SubLibraryId?: string;
  error?: string;
  resultRisk?: string;
  reportRisk?: string;
  reportType?: string;
  ReportType?: string;
  inputFilename?: string;
  sourceFileName?: string;
  product?: string;
}

export interface CustomReportGenerationData {
  Barcode: string;
  PatientName: string;
  PatientDateOfBirth: string;
  GestationWeeks: number;
  GestationDays: number;
  DocumentId: string;
  DateOfVenipuncture: string;
  DateOfConfinement: string;
  SampleReceived?: string;
  DoctorName: string;
  ServiceName: string;
  CountryCode: string;
  LanguageCode: string;
  ApprovedBy: string;
  Gender: boolean;
  IncidentalFindings: boolean;
  productInventoryItemId: string;
  testId: string;
  doctorId: string;
  sampleExtractorId: string;
}

export type ReportGenerationData = [
  ExtendedProductInventoryItem,
  GeneratedReport,
];

export enum ServiceDataStatus {
  generated = 'Generated',
  finished = 'Finished',
  incomplete = 'Incomplete',
}

export enum Risk {
  HighRisk = 'HIGH_RISK',
  LowRisk = 'LOW_RISK',
}

export enum ChromosomeDetection {
  Detected = 'DETECTED',
  NotDetected = 'NOT_DETECTED',
}

export enum ExportDialogType {
  Selected = 'Selected',
  Export = 'Export',
}

export enum ExportDialogDataType {
  Pii = 'pii',
  ServiceData = 'serviceData',
  Sample = 'sample',
  FollowUps = 'followUps',
  Reports = 'reports',
}

export enum ExportType {
  Information = 'Information sheet',
  CancerscreenInformation = 'Cancer screen Information sheet',
  PatientData = 'Patient data',
  BGIInformation = 'BGI Information sheet',
  FullInformation = 'Full Information sheet',
  RequestForms = 'Request forms',
  Invoice = 'Invoice sheet',
  Payment = 'Payment export',
  Analytics = 'Analytics export',
  Insurance = 'Insurance export',
  Report = 'Report export',
  DigitalReports = 'Digital reports export',
  OperationsReports = 'Operations reports export',
  Statistics = 'Statistics export',
  N2DStatistics = 'N2D statistics export',
  N2DCustomerOrders = 'N2D customer orders export',
  Logistics = 'Logistics export',
  SamplesOverview = 'Samples overview export',
  Orders = 'Orders export',
  Consents = 'Consents export',
  Operations = 'Operations export',
  HalosPro = 'Halos Pro',
  Halos = 'Halos',
  BgiPlasma = 'Bgi Plasma',
  Plasma = 'Plasma',
  Tat = 'Tat',
  FollowUps = 'Follow ups',
}

export enum ExportTypeNames {
  BGIInformationSheetAndInformationSheet = 'BGI Information sheet + Information sheet',
  Orders = 'Orders',
  FollowUps = 'Follow ups',
}

export enum Gender {
  Male = 'Male',
  Female = 'Female',
}

export interface StatusResponse {
  StatusUri: string;
  TerminateUri: string;
  InstanceId: string;
}

export enum ServiceDataTerminateReason {
  Miscarriage = 'Miscarriage',
  CanceledByPatient = 'CanceledByPatient',
  CanceledByPartner = 'CanceledByPartner',
  TestLimitation = 'TestLimitation',
}

export interface ValidationPair {
  children?: {
    [key: string]: ValidationPair;
  };
  validators?: any[];
}

export enum PaymentService {
  Revolut = 'Revolut',
  Stripe = 'Stripe',
  Paypal = 'Paypal',
}

export interface CarrierStatus {
  isCarrier: boolean;
}
